import { ReactNode, useEffect } from 'react';
import { CoralogixRum } from '@coralogix/browser';
import { BrowserRouter, Route } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  useApi,
  configApiRef,
  identityApiRef,
  attachComponentData,
} from '@backstage/core-plugin-api';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { RadarPage } from '@backstage-community/plugin-tech-radar';
import { techdocsPlugin, TechDocsReaderPage } from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { DocsReaderLayout } from './components/docs/DocsReaderLayout';
import { TechDocsLayout } from './components/docs/TechDocsLayoutAddon';
import { Root } from './components/Root';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppRoot,
  convertLegacyPageExtension,
  convertLegacyPlugin,
} from '@backstage/core-compat-api';
import {
  ApolloExplorerPage,
  apolloExplorerPlugin,
} from '@backstage-community/plugin-apollo-explorer';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { GithubLoginExtension } from './scaffolder/GithubLoginExtension';
import { meltwaterThemes } from './theme/theme';
import { HomePage } from './components/home/HomePage';
import { CustomCatalogPage } from './components/catalog/CustomCatalogPage';
import { CustomApiExplorerPage } from './components/api/CustomApiExplorerPage';
import { CustomDocsPage } from './components/docs/CustomDocsPage';
import { CustomTeamsExplorerPage } from './components/teams/CustomTeamsExplorerPage';
import { SettingsPage } from './components/settings/SettingsPage';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import {
  ApiBlueprint,
  AppRootElementBlueprint,
  PageBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  RouterBlueprint,
  SignInPageBlueprint,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import { OAuthConsentPage } from './components/oauth/OAuthConsentPage';

const CORALOGIX_PLACEHOLDER_KEY = 'CORALOGIX_PUBLIC_KEY';
let coralogixRumInitialized = false;

const microsoftProvider: SignInProviderConfig = {
  id: 'microsoft-auth-provider',
  title: 'Microsoft',
  message: 'Sign in using SSO',
  apiRef: microsoftAuthApiRef,
};

const appApisModule = createFrontendModule({
  pluginId: 'app',
  extensions: apis.map(factory =>
    ApiBlueprint.make({
      name: factory.api.id,
      params: defineParams => defineParams(factory),
    }),
  ),
});

const appCoreModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ...meltwaterThemes.map(theme =>
      ThemeBlueprint.make({
        name: theme.id,
        params: { theme },
      }),
    ),
    SignInPageBlueprint.make({
      params: {
        loader: async () => props => (
          <SignInPage {...props} auto provider={microsoftProvider} />
        ),
      },
    }),
  ],
});

function AppBrowserRouter(props: { children: ReactNode }) {
  const configApi = useApi(configApiRef);
  const baseUrl = configApi.getOptionalString('app.baseUrl');
  const pathname = baseUrl
    ? new URL(baseUrl, window.location.origin).pathname.replace(/\/$/, '') ||
      '/'
    : '/';
  const basename = pathname === '/' ? undefined : pathname;

  return (
    <BrowserRouter
      basename={basename}
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      {props.children}
    </BrowserRouter>
  );
}

const appRouterModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    RouterBlueprint.make({
      name: 'router',
      params: {
        component: AppBrowserRouter,
      },
    }),
  ],
});

const ApolloExplorerRoute = () => (
  <ApolloExplorerPage
    title="GraphQL"
    subtitle="Federated Graphql Explorer"
    endpoints={[
      {
        title: 'Meltwater Graph - Staging',
        graphRef: 'meltwater-graph@staging',
      },
      {
        title: 'Meltwater Graph - Production',
        graphRef: 'meltwater-graph@prod',
      },
    ]}
  />
);
attachComponentData(
  ApolloExplorerRoute,
  'core.extensionName',
  'ApolloExplorerPage',
);
attachComponentData(
  ApolloExplorerRoute,
  'core.mountPoint',
  apolloExplorerPlugin.routes.root,
);

const ScaffolderCreateRoute = () => (
  <ScaffolderPage
    groups={[
      {
        title: 'Recommended',
        filter: entity =>
          entity?.metadata?.tags?.includes('recommended') ?? false,
      },
    ]}
  >
    <ScaffolderFieldExtensions>
      <GithubLoginExtension />
    </ScaffolderFieldExtensions>
  </ScaffolderPage>
);
attachComponentData(
  ScaffolderCreateRoute,
  'core.extensionName',
  'ScaffolderPage',
);
attachComponentData(
  ScaffolderCreateRoute,
  'core.mountPoint',
  scaffolderPlugin.routes.root,
);

const scaffolderFeature = convertLegacyPlugin(scaffolderPlugin, {
  extensions: [
    convertLegacyPageExtension(ScaffolderCreateRoute, {
      name: 'create',
      path: '/create',
    }),
  ],
});

const appRoutesModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    PageBlueprint.make({
      name: 'home',
      params: {
        path: '/',
        loader: async () => <HomePage />,
      },
    }),
    PageBlueprint.make({
      name: 'tech-radar',
      params: {
        path: '/tech-radar',
        loader: async () => (
          <RadarPage
            width={1500}
            height={800}
            title="Meltwater Tech Radar"
            subtitle="Pick the recommended technologies for your projects"
            pageTitle="Backstage"
          />
        ),
      },
    }),
    PageBlueprint.make({
      name: 'api-docs',
      params: {
        path: '/api-docs',
        loader: async () => <CustomApiExplorerPage />,
      },
    }),
    PageBlueprint.make({
      name: 'teams',
      params: {
        path: '/teams',
        loader: async () => <CustomTeamsExplorerPage />,
      },
    }),
    convertLegacyPageExtension(SettingsPage, {
      name: 'settings',
      path: '/settings',
    }),
    convertLegacyPageExtension(CatalogGraphPage, {
      name: 'catalog-graph',
      path: '/catalog-graph',
    }),
    convertLegacyPageExtension(ApolloExplorerRoute, {
      name: 'apollo-explorer',
      path: '/apollo-explorer',
    }),
  ],
});

const appShellModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootElementBlueprint.make({
      name: 'coralogix-rum',
      params: { element: <CoralogixWrapper /> },
    }),
    AppRootElementBlueprint.make({
      name: 'alert-display',
      params: { element: <AlertDisplay /> },
    }),
    AppRootElementBlueprint.make({
      name: 'oauth-request-dialog',
      params: { element: <OAuthRequestDialog /> },
    }),
  ],
});

function CoralogixWrapper() {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return undefined;
    }

    const publicKey = config.getOptionalString('app.CORALOGIX_PUBLIC_KEY');
    if (!publicKey || publicKey === CORALOGIX_PLACEHOLDER_KEY) {
      return undefined;
    }

    if (!coralogixRumInitialized) {
      CoralogixRum.init({
        public_key: publicKey,
        application: 'backstage',
        version: '0.0.1',
        coralogixDomain: 'EU1',
        sessionConfig: {
          sessionSampleRate: 100,
        },
        sessionRecordingConfig: {
          sessionRecordingSampleRate: 100,
          enable: true,
          autoStartSessionRecording: true,
        },
      });
      coralogixRumInitialized = true;
    }

    let isActive = true;

    async function setUserContext(): Promise<void> {
      const profileInfo = await identityApi.getProfileInfo();
      const backstageUserInfo = await identityApi.getBackstageIdentity();

      if (!isActive) {
        return;
      }

      CoralogixRum.setUserContext({
        user_id: backstageUserInfo.userEntityRef,
        user_name: profileInfo.displayName || 'unknown_user_name',
        user_email: profileInfo.email,
      });
    }

    void setUserContext();

    return () => {
      isActive = false;
    };
  }, [config, identityApi]);

  return null;
}

const routes = (
  <FlatRoutes>
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<CustomDocsPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <TechDocsReaderPage>
          <DocsReaderLayout />
        </TechDocsReaderPage>
      }
    >
      <TechDocsAddons>
        <TechDocsLayout />
        <ReportIssue />
        <Mermaid
          lightConfig={{ theme: 'default' }}
          darkConfig={{ theme: 'dark' }}
        />
      </TechDocsAddons>
    </Route>
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/oauth2/authorize/:sessionId" element={<OAuthConsentPage />} />
  </FlatRoutes>
);

const app = createApp({
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  features: [
    appApisModule,
    appCoreModule,
    appRouterModule,
    appRoutesModule,
    appShellModule,
    scaffolderFeature,
    catalogImportPlugin,
    ...convertLegacyAppRoot(
      <>
        <AppRouter>
          <Root>{routes}</Root>
        </AppRouter>
      </>,
    ),
  ],
});

export default app.createRoot();
